import { WorkflowRoleCode, normalizeRoleCode } from "../../constants/workflow";
import type { WorkflowEmailStage } from "./workflowEmailTemplate";

export type WorkflowStakeholderEmails = {
  customerUserEmail: string;
  customerContactEmail: string;
  implementationTeamEmails: string[];
  tpmEmail: string;
  pmEmail: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value: string): boolean =>
  Boolean(value.trim()) && EMAIL_REGEX.test(value.trim());

const uniqueValidEmails = (
  values: Array<string | undefined | null>,
): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = String(value ?? "").trim();
    if (!isValidEmail(trimmed)) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
};

const parseEmailList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/[,;]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

export const parseWorkflowStakeholderEmails = (
  profile: unknown,
): WorkflowStakeholderEmails | null => {
  if (!profile || typeof profile !== "object") return null;
  const record = profile as Record<string, unknown>;
  const customerRecord =
    record.customer && typeof record.customer === "object"
      ? (record.customer as Record<string, unknown>)
      : null;

  const implementationTeamEmails = uniqueValidEmails([
    ...parseEmailList(record.implementationEmails),
    ...parseEmailList(record.implementationTeamEmails),
    ...(customerRecord
      ? parseEmailList(customerRecord.implementationTeamEmails)
      : []),
  ]);

  return {
    customerUserEmail: String(record.email ?? "").trim(),
    customerContactEmail: String(
      customerRecord?.email ??
        record.customerEmail ??
        record.contactEmail ??
        "",
    ).trim(),
    implementationTeamEmails,
    tpmEmail: String(
      record.technicalProjectManagerEmail ??
        record.tpmEmail ??
        customerRecord?.technicalProjectManagerEmail ??
        customerRecord?.tpmEmail ??
        "",
    ).trim(),
    pmEmail: String(
      record.projectManagerEmail ??
        record.pmEmail ??
        customerRecord?.projectManagerEmail ??
        customerRecord?.pmEmail ??
        "",
    ).trim(),
  };
};

export const getWorkflowEmailStageForRole = (
  roleCode: string | undefined | null,
): WorkflowEmailStage | null => {
  const role = normalizeRoleCode(roleCode);
  if (role === WorkflowRoleCode.CUSTOMER) return "customer_submitted";
  if (role === WorkflowRoleCode.IMPLEMENTATION_TEAM) {
    return "implementation_submitted";
  }
  if (role === WorkflowRoleCode.TPM) return "workflow_complete";
  return null;
};

export const resolveWorkflowEmailRecipients = (
  stage: WorkflowEmailStage,
  stakeholders: WorkflowStakeholderEmails,
  onboardingTeamEmail: string,
): string[] => {
  const internalRecipients = uniqueValidEmails([
    ...stakeholders.implementationTeamEmails,
    stakeholders.tpmEmail,
    stakeholders.pmEmail,
    onboardingTeamEmail,
  ]);
  const everyoneRecipients = uniqueValidEmails([
    stakeholders.customerUserEmail,
    stakeholders.customerContactEmail,
    ...internalRecipients,
  ]);

  switch (stage) {
    case "customer_submitted":
    case "workflow_complete":
      return everyoneRecipients;
    case "implementation_submitted":
      return internalRecipients;
    default:
      return [];
  }
};
